<script setup lang="ts">
import Papa from 'papaparse'
import { DownloadIcon } from '@heroicons/vue/solid'

import { useWalletState, WalletState } from '../../../utilities/account/useWallet'
import WalletTransactionsTable from './WalletTransactionsTable.vue'
import { getTransactionPaymentMethod, getTransactionTitle } from './useWalletTransactions'
import { formatISO9075 } from 'date-fns'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { parseDate } from '../../../utilities/helpers'

const walletState = useWalletState() as WalletState

function downloadFile(filename: string, text: string): void {
    const element = document.createElement('a')
    element.setAttribute('href', `data:text/plain;charset=utf8,${encodeURIComponent(text)}`)
    element.setAttribute('download', filename)

    element.style.display = 'none'
    document.body.appendChild(element)

    element.click()
    document.body.removeChild(element)
}

function exportTransactions(): void {
    const filename = 'conda_report.csv'

    const csvRows = walletState.filteredTransactions.map((transaction) => {
        return {
            'Date': formatISO9075(parseDate(transaction.date)),
            'Title': getTransactionTitle(transaction),
            'Payment Method': getTransactionPaymentMethod(transaction),
            'Amount': transaction.signed_amount,
        }
    })

    const csvString = Papa.unparse(csvRows, {
        quotes: true, //or array of booleans
        delimiter: ';',
        columns: null, //or array of strings
    })

    downloadFile(filename, csvString)
}
</script>

<template>
    <div class="flex items-center justify-between">
        <div class="sm:flex-auto">
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('wallet.transaction_history') }}</h2>
            <p class="text-sm text-gray-700">{{ $t('wallet.transaction_history_subtitle') }}</p>
        </div>
        <div class="justify-end sm:flex sm:flex-auto">
            <div>
                <BaseButtonMuted :disabled="walletState.filteredTransactions.length === 0" @click="exportTransactions">
                    <DownloadIcon class="mr-2 w-4" />{{ $t('wallet.transaction_history_export_button') }}
                </BaseButtonMuted>
            </div>
        </div>
    </div>
    <div
        v-if="walletState.filteredTransactions.length > 0"
        class="mb-6 mt-3 bg-white shadow sm:overflow-hidden sm:rounded-lg">
        <div class="">
            <WalletTransactionsTable :transactions="walletState.filteredTransactions" />
        </div>
    </div>
    <div v-else class="mt-4 text-center">
        <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true">
            <path
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('wallet.no_transactions') }}</h3>
        <p class="mt-1 text-sm text-gray-500" v-html="$t('wallet.no_transactions_description')"></p>
    </div>
</template>
